import { Fragment, useContext } from 'react';
import { Box, Divider, Container } from 'theme-ui';
import { DeliveryVerificationMethod, UserDeliveryRequirements } from '../../contexts/contextTypes';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../contexts/AppContext';

type SwitchProps = {
  userRequirements: string | undefined;
  partnerRequirements: {
    identificationCheckRequired: boolean | null | undefined;
    recipientMinimumAge: number | null | undefined;
    recipientMustMatchEndCustomer: boolean | null | undefined;
    recipientMustMatchEndCustomerWithBankIDQR: boolean | null | undefined;
    requireSignature: boolean | null | undefined;
  };
};

const styleSheet = `
    @keyframes blink {
      25% { opacity: 0.5; }
    }
  `;

// Define inline styles for the blinking text
const blinkingStyle = {
  animation: 'blink 1s step-start infinite'
  // color: 'blue'
};

const DeliveryRequirementBadge = ({ userRequirements, partnerRequirements }: SwitchProps) => {
  const { t } = useTranslation();
  const { trackingDetails, settings } = useContext(AppContext);

  const verifyNameByBankIdCode = settings?.deliveryVerificationMethods?.['checkName']
    ? Object.values(settings.deliveryVerificationMethods['checkName']).includes(DeliveryVerificationMethod.BANKID_CODE)
    : false;

  const verifyNameByBankIdQR = settings?.deliveryVerificationMethods?.['checkName']
    ? Object.values(settings.deliveryVerificationMethods['checkName']).includes(DeliveryVerificationMethod.BANKID_QR)
    : false;

  const prioritizedCheckNameMethod = settings?.deliveryVerificationMethods?.['checkName']?.['1'] || null;

  // If checkName only has BANKID_CODE, return null as BankIDVerification component handles it
  // If both methods exist, use the prioritized method (index '1') to decide
  if (partnerRequirements.recipientMustMatchEndCustomer && verifyNameByBankIdCode) {
    if (!verifyNameByBankIdQR || prioritizedCheckNameMethod === DeliveryVerificationMethod.BANKID_CODE) {
      return null;
    }
  }

  const verifyAgeByBankIdQR = settings?.deliveryVerificationMethods?.['checkAge']
    ? Object.values(settings.deliveryVerificationMethods['checkAge']).includes(DeliveryVerificationMethod.BANKID_QR)
    : false;
  const verifyByDrivingLicenseQR = settings?.deliveryVerificationMethods?.['checkAge']
    ? Object.values(settings.deliveryVerificationMethods['checkAge']).includes(DeliveryVerificationMethod.DRIVING_LICENSE_QR)
    : false;

  const getDeliveryVerificationMessage = (): string => {
    if (userRequirements !== UserDeliveryRequirements.IN_PERSON) {
      return t('deliveryRequirements.contactless');
    }

    const minimumAge = trackingDetails?.task?.recipientMinimumAge || 18;
    const requiresBankIdQrAgeVerification = verifyAgeByBankIdQR && partnerRequirements.recipientMinimumAge;
    const requiresDrivingLicenseQrAgeVerification = verifyByDrivingLicenseQR && partnerRequirements.recipientMinimumAge;

    if (partnerRequirements.recipientMinimumAge) {
      const key = requiresBankIdQrAgeVerification
        ? 'deliveryRequirements.requireMinimumAgeWithBankId'
        : requiresDrivingLicenseQrAgeVerification
          ? 'deliveryRequirements.requireMinimumAgeWithDrivingLicense'
          : 'deliveryRequirements.requireMinimumAge';
      return t(key).replace(/(##minimumAge##)/, minimumAge.toString());
    }

    if (
      partnerRequirements.identificationCheckRequired ||
      partnerRequirements.recipientMustMatchEndCustomer ||
      partnerRequirements.recipientMustMatchEndCustomerWithBankIDQR
    ) {
      return verifyNameByBankIdQR ? t('deliveryRequirements.identificationCheckRequiredWithBankIdQR') : t('deliveryRequirements.identificationCheckRequired');
    }

    return t('deliveryRequirements.requireSignature');
  };

  if (userRequirements !== UserDeliveryRequirements.IN_PERSON) {
    return (
      <Fragment>
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            sx={{
              p: '5px 5px',
              textAlign: 'center',
              fontSize: '15px'
            }}
          >
            <style>{styleSheet}</style>
            <span>{t('deliveryRequirements.contactless')}</span>
          </Box>
        </Container>
        <Divider />
      </Fragment>
    );
  }

  const deliveryVerificationMessage = getDeliveryVerificationMessage();

  return (
    <Fragment>
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box
          sx={{
            p: '10px 15px',
            textAlign: 'center',
            fontSize: '15px',
            color: '#CC5500'
          }}
        >
          <style>{styleSheet}</style>
          <span style={blinkingStyle}>{deliveryVerificationMessage}</span>
        </Box>
      </Container>
      <Divider />
    </Fragment>
  );
};

export default DeliveryRequirementBadge;
